import { useLayoutEffect } from 'react';

/**
 * Hook to activate scroll-reveal animations on the current page.
 *
 * Problem: `HomePage` adds `js-animations-ready` class to `<html>`, which sets
 * `opacity: 0` on all `[data-reveal]` elements globally (via index.scss).
 * Pages that don't run their own IntersectionObserver (e.g. TinTuc, LienHe)
 * never get `data-visible="true"` → elements stay invisible after SPA navigation.
 *
 * This hook must be called from any page that renders `[data-reveal]` elements,
 * including the shared `<Footer />` component.
 */
export function useRevealAnimation() {
  useLayoutEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).setAttribute('data-visible', 'true');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0, rootMargin: '200px 0px 200px 0px' }
    );

    document.documentElement.classList.add('js-animations-ready');

    document
      .querySelectorAll('[data-reveal], [data-stagger]')
      .forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);
}
